<template>
  <FullPageLayout>
    <template #page__actions>
      <ArticleFavorite :article="article" />
      <ScrollToComments :article="article" is-custom-article />
      <ArticleShare :article="article" is-custom-article />
    </template>
    <template v-if="fetchState.error">
      <ErrorPageLayout :error="fetchState.error" />
    </template>
    <Astrology v-else-if="!isEmpty(article)" :article="article" :horoscope="horoscope" :date="date" :type="type" :frequency="frequency" />
  </FullPageLayout>
</template>

<script>
import { defineComponent, useMeta, useFetch, useRoute, useContext, ref, provide, ssrRef } from '@nuxtjs/composition-api';
import { initializeAdLoaders } from '@/modules/ads/adUtils';
import { isEmpty } from 'lodash';
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout';
import { useAuth } from '~/modules/auth/state/useAuth';
import { IS_ONEDIO } from '~/constants/config';
import { useArticleMeta } from '~/modules/article/utils/articleMeta';
import { parseHoroscopeUrl } from '~/modules/astrology/utils/astrologyUtils';
import { useArticle } from '~/modules/article/composables/useArticle';
import ArticleFavorite from '~/modules/article/components/sticky/ArticleFavorite';
import ArticleShare from '~/modules/article/components/sticky/ArticleShare';
import ScrollToComments from '~/modules/common/components/app/layout/ScrollToComments';
import Astrology from '~/modules/astrology/components/Astrology.vue';
import { articleTitle } from '~/modules/article/utils/articleUtils';

export default defineComponent({
  components: {
    FullPageLayout,
    ErrorPageLayout,
    ArticleFavorite,
    ArticleShare,
    ScrollToComments,
    Astrology,
  },
  head: {
    bodyAttrs: { class: 'o-ps-global' },
  },
  setup() {
    const { generateAdConfig } = initializeAdLoaders();
    const serversideAdConfig = generateAdConfig('Article');
    const { $service, $error, $cache, $redirect, i18n } = useContext();

    const { isLoggedIn } = useAuth();
    const { sendPageView } = useArticle();

    const article = ref({});
    const isSponsored = ssrRef(false);
    const horoscope = ssrRef(null);
    const date = ssrRef(null);
    const frequency = ssrRef(null);
    const type = ssrRef(null);

    // page info
    const route = useRoute();
    const pageInfo = parseHoroscopeUrl(route.value.path);
    horoscope.value = pageInfo?.horoscope;
    date.value = pageInfo?.date;
    frequency.value = pageInfo?.frequency;
    type.value = pageInfo?.type;
    // console.log('pageInfo', pageInfo);

    const { fetch, fetchState } = useFetch(async () => {
      try {
        if (!pageInfo || !IS_ONEDIO) {
          $error('Page not found', 404, false);
        }
        if (!horoscope.value || !date.value || !frequency.value || !type.value) {
          $error('No legacy horoscope data passed', 404, false);
        }

        const { data } = await $service.article.getAstrologyArticle({
          horoscope: horoscope.value,
          date: date.value,
          frequency: frequency.value,
          type: type.value,
        });
        article.value = data;
        $cache(data?.flags?.isDraft ? 'no-cache' : 'public, max-age=60');
        const adConfig = serversideAdConfig(article);
        isSponsored.value = adConfig.value?.decidedMode?.value === 'sponsored';
        if (article.value?.redirectUrl) {
          $redirect(article.value);
        }
      } catch (error) {
        $error(error);
      }
    });

    // meta
    useMeta(() => ({ title: articleTitle(article.value) || i18n.t('news-page.no-content') }));
    useArticleMeta({ article });
    sendPageView(article, isLoggedIn.value, isSponsored.value);

    provide('article', article);

    return {
      fetch,
      fetchState,
      isEmpty,
      article,
      horoscope,
      date,
      frequency,
      type,
    };
  },
});
</script>
